/**
 * AppShell — единый layout для всех страниц после auth.
 *
 * Mobile-first: bottom-nav (6 секций) + top-bar с названием экрана и
 * кнопкой переключения темы. На больших экранах превращается в sidebar.
 *
 * Тема (dark / light) управляется useTheme в lib/theme.ts. По
 * умолчанию dark — все классы tailwind через `dark:` префикс на
 * корневом <html>.
 *
 * Ссылка «Админка» в top-bar (B-2517) — у тех, у кого есть доступ в панель
 * (админ или ментор): из админки в кабинет ведёт «ЛК ученика», это путь назад.
 */

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api } from '../lib/api';
import { ADMIN_URL, hasAdminPanelAccess, redirectToDrafterId } from '../lib/drafter-auth';
import { clearAllClientState } from '../lib/token-storage';
import { useTheme } from '../lib/theme';
import type { MeResponse } from '../types/cabinet';
import {
  BookIcon,
  BriefcaseIcon,
  ChatIcon,
  HomeIcon,
  NotebookIcon,
  SettingsIcon,
  TimelineIcon,
} from './Icons';

// ChatIcon — оставлен в экспорте Icons, роут работает,
// убран из основной навигации (лимит bottom-nav).
void ChatIcon;

interface NavItem {
  label: string;
  to: string;
  icon: (props: { className?: string }) => JSX.Element;
}

const NAV: NavItem[] = [
  { label: 'Главная', to: '/', icon: HomeIcon },
  { label: 'Таймлайн', to: '/timeline', icon: TimelineIcon },
  { label: 'Заметки', to: '/notes', icon: NotebookIcon },
  { label: 'Услуги', to: '/services', icon: BriefcaseIcon },
  { label: 'Материалы', to: '/resources', icon: BookIcon },
  { label: 'Настройки', to: '/settings', icon: SettingsIcon },
];

interface AppShellProps {
  title: string;
  children: ReactNode;
  /** Дополнительный заголовок справа от title — например ID объекта. */
  meta?: ReactNode;
}

export function AppShell({ title, children, meta }: AppShellProps) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-[100dvh] bg-slate-50 pb-20 transition-colors dark:bg-slate-950 md:pb-0">
      <Header title={title} meta={meta} />
      <div className="md:flex">
        <Sidebar pathname={pathname} />
        <main className="flex-1">
          <div className="mx-auto max-w-3xl px-4 py-5 sm:py-8">{children}</div>
          <LegalFooter />
        </main>
      </div>
      <BottomNav pathname={pathname} />
    </div>
  );
}

function Header({ title, meta }: { title: string; meta?: ReactNode }) {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 md:max-w-none md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <img
            src="/logo/mark.svg"
            alt="DRAFTER"
            className="h-8 w-8 shrink-0 rounded-md bg-slate-900 p-0.5 dark:bg-slate-800"
          />
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-widest text-slate-400 dark:text-slate-500">
              DRAFTER
            </p>
            <h1 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h1>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {meta && <div className="text-xs text-slate-500 dark:text-slate-400">{meta}</div>}
          <AdminPanelLink />
          <ThemeToggle />
          <LogoutButton />
        </div>
      </div>
    </header>
  );
}

/**
 * Переход в админку для админа и ментора (B-2517). Профиль — тот же запрос
 * `['me']`, что у страниц, поэтому лишнего похода на бэкенд нет. Пока профиль
 * грузится или не пришёл — ссылки нет. Обычная ссылка, а не `Link`: админка —
 * другое приложение и другой origin, сеанс там поднимет SSO-cookie.
 */
function AdminPanelLink() {
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse> => (await api.get<MeResponse>('/v1/cabinet/me')).data,
  });
  if (!hasAdminPanelAccess(me)) return null;
  return (
    <a
      href={ADMIN_URL}
      title="Перейти в админку акселератора"
      className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-slate-100"
    >
      Админка
    </a>
  );
}

function LogoutButton() {
  return (
    <button
      type="button"
      onClick={() => {
        // Чистим локальное состояние и уходим в Drafter ID (централизованный
        // вход/выход). Общую httpOnly-cookie `.drafter.ru` рвёт только IdP.
        clearAllClientState();
        redirectToDrafterId();
      }}
      aria-label="Выйти"
      title="Выйти из кабинета"
      className="grid h-9 w-9 place-items-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    </button>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Светлая тема' : 'Тёмная тема'}
      title={isDark ? 'Светлая тема' : 'Тёмная тема'}
      className="grid h-9 w-9 place-items-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
    >
      {isDark ? (
        // Sun icon
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // Moon icon
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function BottomNav({ pathname }: { pathname: string }) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:hidden">
      <ul className="flex">
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = isActive(pathname, item.to);
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                className={`flex flex-col items-center gap-0.5 py-2 text-[10px] transition ${
                  active
                    ? 'text-brand-700 dark:text-brand-400'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Icon
                  className={`h-6 w-6 ${
                    active
                      ? 'text-brand-700 dark:text-brand-400'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                />
                <span className="font-medium">{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function Sidebar({ pathname }: { pathname: string }) {
  return (
    <aside className="hidden w-56 shrink-0 border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:block">
      <nav className="sticky top-[57px] px-3 py-4">
        <ul className="space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(pathname, item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${
                    active
                      ? 'bg-brand-50 font-medium text-brand-800 dark:bg-brand-500/15 dark:text-brand-300'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(to + '/');
}

function LegalFooter() {
  return (
    <footer className="mt-12 border-t border-slate-200 px-4 pb-8 pt-6 text-center text-[11px] text-slate-500 dark:border-slate-800 dark:text-slate-500">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-1">
        <a
          href="https://accelerator.drafter.ru/legal/privacy-policy.html"
          target="_blank"
          rel="noopener"
          className="hover:text-slate-800 dark:hover:text-slate-300"
        >
          Политика ПДн
        </a>
        <span aria-hidden>·</span>
        <a
          href="https://accelerator.drafter.ru/legal/public-offer.html"
          target="_blank"
          rel="noopener"
          className="hover:text-slate-800 dark:hover:text-slate-300"
        >
          Публичная оферта
        </a>
        <span aria-hidden>·</span>
        <a
          href="https://accelerator.drafter.ru/legal/user-agreement.html"
          target="_blank"
          rel="noopener"
          className="hover:text-slate-800 dark:hover:text-slate-300"
        >
          Соглашение
        </a>
        <span aria-hidden>·</span>
        <a
          href="https://accelerator.drafter.ru/legal/cookies.html"
          target="_blank"
          rel="noopener"
          className="hover:text-slate-800 dark:hover:text-slate-300"
        >
          Cookies
        </a>
      </div>
      <p className="mt-2">© 2026 ИП Смирнов А.А. · ОГРНИП 320583500001647</p>
    </footer>
  );
}
